/**
 * OdeslatZpravuModal — Phase 11.6.10 message quick-add dialog.
 *
 * Posts to
 * `POST /v1/contacts/{id}/messages` which appends a `kontakt_zpravy` row
 * AND updates the parent contact's `posledni_kontakt` + `status`.
 */

import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Field, Fields } from '@/components/Field';
import { DialogCrown, Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useCreateContactMessageMutation } from './api';

export interface OdeslatZpravuModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: number;
  contactLabel?: string;
  /** Pre-fill status from the contact's current status field. */
  defaultStatus?: string;
}

function todayIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function OdeslatZpravuModal({
  open,
  onOpenChange,
  contactId,
  contactLabel,
  defaultStatus,
}: OdeslatZpravuModalProps) {
  const [datum, setDatum] = useState(todayIso());
  const [zprava, setZprava] = useState('');
  const [status, setStatus] = useState(defaultStatus ?? '');
  const [bev, setBev] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDatum(todayIso());
    setZprava('');
    setStatus(defaultStatus ?? '');
    setBev(false);
  }, [open, defaultStatus]);

  const createMutation = useCreateContactMessageMutation(contactId);
  const isPending = createMutation.isPending;
  const canSubmit = useMemo(
    () => datum.length === 10 && zprava.trim().length > 0 && !isPending,
    [datum, zprava, isPending],
  );

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      await createMutation.mutateAsync({
        zprava: zprava.trim(),
        datum,
        status: status.trim() || undefined,
        nabidnuto_bev: bev ? 1 : 0,
      });
      toast.ok('Zpráva uložena');
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Zprávu se nepodařilo uložit';
      toast.error(message);
    }
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange} maxWidth={620}>
      <DialogCrown
        tone="neutral"
        eyebrow="Nová zpráva"
        eyebrowMeta={contactLabel ? <>№ {contactId} · {contactLabel}</> : <>№ {contactId}</>}
        title={
          <>
            Přidej <em>zprávu klientovi</em>
          </>
        }
        sub={<>Datum, status a obsah komunikace. Posledni kontakt se přepíše datem zprávy.</>}
      />
      <Modal.Body>
        <Modal.Section>
          <Fields cols={2}>
            <Field label="Datum kontaktu" required>
              <TextInput
                type="date"
                value={datum}
                onChange={(e) => setDatum(e.currentTarget.value)}
                data-testid="zprava-datum"
              />
            </Field>
            <Field label="Status" optional="krátký stav klienta">
              <TextInput
                value={status}
                onChange={(e) => setStatus(e.currentTarget.value)}
                placeholder="např. Po test. jízdě"
                data-testid="zprava-status"
              />
            </Field>
            <Field label="Zpráva" required span>
              <Textarea
                rows={6}
                value={zprava}
                onChange={(e) => setZprava(e.currentTarget.value)}
                placeholder="Shrnutí komunikace s klientem…"
                data-testid="zprava-text"
              />
            </Field>
          </Fields>
          <div className="mt-4">
            <Checkbox
              checked={bev}
              onChange={(e) => setBev(e.currentTarget.checked)}
              label="Nabídnuta jízda BEV"
              data-testid="zprava-bev"
            />
          </div>
        </Modal.Section>
      </Modal.Body>
      <Modal.Foot
        meta={<>Esc — zavřít · ⏎ — uložit</>}
        actions={
          <>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              mono
              onClick={() => onOpenChange(false)}
            >
              Zrušit
            </Button>
            <Button
              type="button"
              variant="ink"
              size="sm"
              mono
              disabled={!canSubmit}
              onClick={handleSubmit}
              data-testid="zprava-save"
            >
              {isPending ? 'Ukládám…' : 'Přidat zprávu'}
            </Button>
          </>
        }
      />
    </Modal>
  );
}
